import { parseWholeDollarCurrencyInput } from './currency'
import { mobileDigitsForComparison } from './validation'
import type { PendingListedDriverApiPayload, PendingUpdateListedDriverStored } from './pendingListedDriver'

export interface ComputeFieldChangesInput {
  initialRegistrationNumber: string | null
  registrationNumber: string
  initialState: string | null
  state: string
  initialParkingAddress: string | null
  parkingAddress: string
  initialOvernightParking: string | null
  parkingLocation: string
  initialModificationsValue: string | null
  modificationsValue: string
  initialOffRoadRecovery: string | null
  offRoadRecovery: string
  initialEmail: string | null
  email: string
  initialMobileNumber: string | null
  mobileNumber: string
  initialPostalAddress: string | null
  postalAddress: string
  pendingNewListedDriver: PendingListedDriverApiPayload | null
  /** Listed driver edit pending submit (update-all flow). */
  pendingUpdateListedDriver: PendingUpdateListedDriverStored | null
}

export interface PolicyFieldChanges {
  regoFrom: string
  regoTo: string
  regoChanged: boolean
  stateFrom: string
  stateTo: string
  stateChanged: boolean
  parkingAddrFrom: string
  parkingAddrTo: string
  parkingAddrChanged: boolean
  overnightFrom: string
  overnightTo: string
  overnightChanged: boolean
  modFrom: number | null
  modTo: number | null
  modChanged: boolean
  /** Raw initial value for display (option lookup). */
  recoveryFrom: string
  /** Raw current value for display (option lookup). */
  recoveryTo: string
  /** Compared using the same defaults as modify payload (1500 when unset). */
  recoveryChanged: boolean
  emailFrom: string
  emailTo: string
  emailChanged: boolean
  mobileChanged: boolean
  postalFrom: string
  postalTo: string
  postalChanged: boolean
  addingListedDriver: boolean
  updatingListedDriver: boolean
  anyChange: boolean
}

/**
 * Single source of truth for which policy fields differ from their initial values,
 * used by the update-all confirm UI and submit payload shaping.
 */
export function computeFieldChanges(input: ComputeFieldChangesInput): PolicyFieldChanges {
  const {
    initialRegistrationNumber,
    registrationNumber,
    initialState,
    state,
    initialParkingAddress,
    parkingAddress,
    initialOvernightParking,
    parkingLocation,
    initialModificationsValue,
    modificationsValue,
    initialOffRoadRecovery,
    offRoadRecovery,
    initialEmail,
    email,
    initialMobileNumber,
    mobileNumber,
    initialPostalAddress,
    postalAddress,
    pendingNewListedDriver,
    pendingUpdateListedDriver,
  } = input

  const regoFrom = initialRegistrationNumber ?? ''
  const regoTo = registrationNumber?.trim() ?? ''
  const regoChanged = regoFrom.trim() !== regoTo

  const stateFrom = (initialState ?? '').trim()
  const stateTo = state?.trim() ?? ''
  const stateChanged = stateFrom !== stateTo

  const parkingAddrFrom = initialParkingAddress ?? ''
  const parkingAddrTo = parkingAddress?.trim() ?? ''
  const parkingAddrChanged = parkingAddrFrom.trim() !== parkingAddrTo

  const overnightFrom = (initialOvernightParking ?? '').trim()
  const overnightTo = parkingLocation?.trim() ?? ''
  const overnightChanged = overnightFrom !== overnightTo

  const modFrom =
    initialModificationsValue != null && initialModificationsValue !== ''
      ? Number(initialModificationsValue)
      : null
  const modToCents = modificationsValue?.trim() ? parseWholeDollarCurrencyInput(modificationsValue) : null
  const modTo = modToCents != null ? modToCents / 100 : null
  const modChanged = modFrom !== modTo

  const recoveryFrom = initialOffRoadRecovery ?? ''
  const recoveryTo = offRoadRecovery?.trim() ?? ''
  const recoveryNormFrom = initialOffRoadRecovery ?? '1500'
  const recoveryNormTo = offRoadRecovery?.trim() || '1500'
  const recoveryChanged = recoveryNormFrom !== recoveryNormTo

  const emailFrom = (initialEmail ?? '').trim()
  const emailTo = email?.trim() ?? ''
  const emailChanged = emailFrom !== emailTo

  const mobileChanged =
    mobileDigitsForComparison(initialMobileNumber ?? '') !==
    mobileDigitsForComparison(mobileNumber ?? '')

  const postalFrom = (initialPostalAddress ?? '').trim()
  const postalTo = postalAddress?.trim() ?? ''
  const postalChanged = postalFrom !== postalTo

  const addingListedDriver = pendingNewListedDriver != null
  const updatingListedDriver = pendingUpdateListedDriver != null

  const anyChange =
    regoChanged ||
    stateChanged ||
    parkingAddrChanged ||
    overnightChanged ||
    modChanged ||
    recoveryChanged ||
    emailChanged ||
    mobileChanged ||
    postalChanged ||
    addingListedDriver ||
    updatingListedDriver

  return {
    regoFrom,
    regoTo,
    regoChanged,
    stateFrom,
    stateTo,
    stateChanged,
    parkingAddrFrom,
    parkingAddrTo,
    parkingAddrChanged,
    overnightFrom,
    overnightTo,
    overnightChanged,
    modFrom,
    modTo,
    modChanged,
    recoveryFrom,
    recoveryTo,
    recoveryChanged,
    emailFrom,
    emailTo,
    emailChanged,
    mobileChanged,
    postalFrom,
    postalTo,
    postalChanged,
    addingListedDriver,
    updatingListedDriver,
    anyChange,
  }
}
